import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import PageHero from '@/components/feature/PageHero';
import { videos } from '@/mocks/home';
import { fetchVideoList } from '@/services/video';
import { adaptVideos } from '@/services/adapters';
import { useRequest } from '@/hooks/useRequest';

const HERO_IMAGE =
  'https://readdy.ai/api/search-image?query=Cinematic%20warm%20studio%20scene%20symbolizing%20documentary%20filming%20of%20traditional%20Chinese%20medicine%2C%20soft%20golden%20light%20rays%2C%20blurred%20herbal%20props%20in%20foreground%2C%20deep%20warm%20tones%20with%20dark%20lower%20area%20for%20text%20overlay%2C%20editorial%20photography&width=1920&height=560&seq=videos-hero&orientation=landscape';

export default function VideosPage() {
  // 接口失败时降级为 mock 数据
  const { data: videoList } = useRequest(
    () => fetchVideoList({ pageIndex: 1, pageSize: 999 }).then((res) => adaptVideos(res.list)),
    { fallback: videos },
  );

  const categories = useMemo(
    () => ['全部', ...Array.from(new Set(videoList.map((item) => item.category)))],
    [videoList],
  );
  const [active, setActive] = useState('全部');

  const filtered = active === '全部' ? videoList : videoList.filter((item) => item.category === active);

  return (
    <PageShell>
      <PageHero
        image={HERO_IMAGE}
        eyebrow="视频中心"
        title="国医风云录，影像中的传承"
        description="以纪录片视角走近国医大师，记录中西医结合之路的探索与实践，让名医经验与临床智慧可看、可感、可传承。"
        breadcrumb={[{ label: '首页', to: '/' }, { label: '视频中心' }]}
      />

      <section className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center gap-2 mb-8">
            {categories.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setActive(item)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  active === item
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-800'
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {filtered.map((video) => (
              <Link key={video.id} to={`/videos/${video.id}`} className="group cursor-pointer">
                <div className="relative w-full h-[180px] rounded-lg overflow-hidden mb-4">
                  <img
                    src={video.image}
                    alt={video.title}
                    title={`${video.title} - 视频专区`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-foreground-950/25 group-hover:bg-foreground-950/40 transition-colors"></div>
                  <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-background-50/90 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                    <i className="ri-play-fill text-2xl"></i>
                  </span>
                  {video.duration && (
                    <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-foreground-950/70 text-background-50 text-xs">
                      {video.duration}
                    </span>
                  )}
                  <span className="absolute top-2 left-2 px-2.5 py-0.5 rounded-full bg-background-50/90 backdrop-blur text-xs font-medium text-accent-700">
                    {video.category}
                  </span>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 leading-snug mb-3 line-clamp-2 group-hover:text-primary-600 transition-colors">
                  {video.title}
                </h3>
                <div className="flex items-center justify-end text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {video.views}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}